import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Lock, PencilRuler, Star, Target } from 'lucide-react';
import { instructorApi } from '../../api/instructor';
import { useUiStore } from '../../store/ui';
import { useAuthStore } from '../../store/auth';
import { clearSessionNotes } from '../../lib/sessionNotes';
import { fmtDate } from '../../lib/dates';
import { lessonInProgress } from '../../lib/lessonInProgress';
import { Button, Card, ConfirmDialog, ProgressBar, SelectField, TextAreaField } from '../../components/charte';
import { PageShell } from '../../components/shell/PageShell';
import { PrivateNote } from '../../components/lesson/PrivateNote';
import { SessionNotesCard } from '../../components/lesson/SessionNotesCard';
import { ReferentialBooklet } from '../livret/ReferentialBooklet';
import { BilanItem } from '../livret/BilanItem';

// Bilan de leçon en page (refonte du 14/10/2026, tablette d'abord) : mêmes champs et même envoi qu'avant
// (identifiant de requête gardé pour un nouvel essai sans doublon), plus de place, rappel du bilan précédent.
// Champs (demande du 14/10/2026) : objectif du prochain cours et synthèse obligatoires ; signalement au
// secrétariat et motif de l'avis examen facultatifs ; compétences travaillées retirées.
// 21/10/2026 : appelé « Résumé de la séance » dans l'application ; commentaire privé (moniteurs seulement) ;
// ce que l'élève voit dans son espace (synthèse, note, prêt pour l'examen) est signalé « Vu par l'élève ».
// J31 (28/10/2026) : les notes prises avant et pendant la séance (Préparer le cours) sont rappelées à côté, reprises
// dans la synthèse d'un toucher, et effacées de l'appareil une fois le résumé enregistré.
// 29/10/2026 : pendant la séance, la leçon en cours est choisie d'office, modifiable (J32) ; le motif de l'avis
// d'examen n'apparaît qu'une fois l'élève dit prêt (J33) ; commentaire privé « Visible par les moniteurs de
// l'élève » : les autres moniteurs qui le suivent le lisent aussi (J34, #482).
// 30/10/2026 : « Livret et résumé de la séance », une seule page à la place du livret (« Préparer le cours ») et du
// résumé : en tête « Objectif de ce cours » et « Mes notes de la séance » (modifiables), à la place des rappels
// « Objectif fixé la dernière fois » et des notes en lecture seule ; puis le référentiel de compétences (enregistré à
// chaque choix), le résumé et, à côté, les résumés des séances. Avant le début de la leçon désignée, le serveur refuse
// le résumé : il ne s'enregistre pas encore (livret et notes, oui).

/** Note de la séance (J36) : ce qu'elle mesure, rappelé sous les étoiles. */
const SESSION_RATING_MEANING = "Sur l'attitude d'ensemble de l'élève pendant la séance, pas sur sa conduite. Note indicative.";

/** Ce champ apparaît dans l'espace de l'élève. */
function SeenByStudent() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-pill bg-surface-2 px-2 py-0.5 text-[11.5px] font-medium text-secondary">
      <Eye size={12} aria-hidden />
      Vu par l'élève
    </span>
  );
}

export default function BilanPage() {
  const target = useUiStore((s) => s.bilanTarget)!;
  const close = useUiStore((s) => s.closeBilan);
  const openBoard = useUiStore((s) => s.openBoard);
  const userId = useAuthStore((s) => s.user?.id);
  const queryClient = useQueryClient();

  const [summary, setSummary] = useState('');
  // Leçon choisie dans la liste ; `null` tant que rien n'est choisi (la leçon en cours le sera d'office).
  const [chosenLesson, setChosenLesson] = useState<string | null>(null);
  const [nextObjective, setNextObjective] = useState('');
  const [secretaryAlert, setSecretaryAlert] = useState('');
  const [examReason, setExamReason] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [command, setCommand] = useState<{ hash: string; id: string } | null>(null);
  const [rating, setRating] = useState(0);
  // Note de la séance réglée par l'auto-école (Réglages › App moniteur, J39) : retirée ou non montrée seulement
  // sur un réglage lu et explicite ; proposée tant que le réglage n'est pas lu ou s'il est illisible.
  const appSettings = useQuery({ queryKey: ['instructor-app-settings'], queryFn: instructorApi.getAppSettings, staleTime: 60_000 });
  const ratingOffered = appSettings.data?.sessionRating?.enabled !== false;
  const ratingShared = appSettings.data?.sessionRating?.shared !== false;
  const [readyForExam, setReadyForExam] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const lessons = useQuery({
    queryKey: ['student-lessons', target.candidateId],
    queryFn: () => instructorApi.getStudentLessons(target.candidateId),
    enabled: !target.lessonId,
  });
  // Bilan précédent : l'objectif fixé pour ce cours, pour l'avoir sous les yeux.
  const student = useQuery({
    queryKey: ['student', target.candidateId],
    queryFn: () => instructorApi.getStudent(target.candidateId),
  });
  const bilans = [...(student.data?.bilans ?? [])].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const previous = bilans[0];
  // Leçon désignée pas encore commencée ; la page se relit à son début, sans attendre une saisie.
  const notStarted = !!target.lessonStartsAt && Date.parse(target.lessonStartsAt) > Date.now();
  const [, rerender] = useState(0);
  useEffect(() => {
    const wait = target.lessonStartsAt ? Date.parse(target.lessonStartsAt) - Date.now() : 0;
    if (wait <= 0) return;
    const timer = setTimeout(() => rerender((n) => n + 1), Math.min(wait + 500, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [target.lessonStartsAt]);
  const hours = student.data && `${student.data.drivingHoursDone.toLocaleString('fr-FR')}/${student.data.drivingHoursTarget} h de conduite`;

  const mutation = useMutation({
    mutationFn: instructorApi.createBilan,
    onSuccess: () => {
      for (const key of ['today', 'planning', 'students', 'student-lessons', 'student'])
        void queryClient.invalidateQueries({ queryKey: [key] });
      clearSessionNotes(userId, target.candidateId);
      close();
    },
  });

  // Autre élève ou autre leçon : formulaire vierge.
  const resetMutation = mutation.reset;
  useEffect(() => {
    setSummary('');
    setChosenLesson(null);
    setNextObjective('');
    setSecretaryAlert('');
    setExamReason('');
    setPrivateNote('');
    setCommand(null);
    setRating(0);
    setReadyForExam(false);
    resetMutation();
  }, [target.candidateId, target.lessonId, resetMutation]);
  // Pendant la séance : la leçon en cours, choisie une fois la liste lue (après la remise à zéro ci-dessus,
  // déjà en cache ou non) ; la liste reste modifiable.
  useEffect(() => {
    if (chosenLesson === null && lessons.data) setChosenLesson(lessonInProgress(lessons.data)?.id ?? '');
  }, [chosenLesson, lessons.data]);

  const dirty = !!(summary || nextObjective || secretaryAlert || examReason || privateNote || rating || readyForExam);
  const back = () => (dirty && !mutation.isPending ? setLeaving(true) : close());

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (notStarted || !summary.trim() || !nextObjective.trim()) return;
    const payload = {
      candidateId: target.candidateId,
      lessonId: target.lessonId || chosenLesson || '',
      summary: summary.trim(),
      rating: (ratingOffered && rating) || undefined,
      readyForExam,
      nextObjective,
      secretaryAlert,
      // Champ masqué quand l'élève n'est pas dit prêt : son texte n'est pas envoyé.
      examReason: readyForExam ? examReason : '',
      privateNote,
    };
    const hash = JSON.stringify(payload),
      requestId = command?.hash === hash ? command.id : crypto.randomUUID();
    setCommand({ hash, id: requestId });
    mutation.mutate({ ...payload, requestId });
  };

  const formId = 'resume-de-seance';
  return (
    <PageShell
      eyebrow={target.candidateName}
      title="Livret et résumé de la séance"
      subtitle={[hours, 'Ce résumé ne modifie pas le livret officiel RDVPermis.'].filter(Boolean).join(' · ')}
      onBack={back}
      actions={
        // Fin de leçon : expliquer sur le tableau, puis revenir au bilan tel qu'il était.
        <Button icon={PencilRuler} onClick={openBoard}>
          Expliquer sur le tableau
        </Button>
      }
      footer={
        <>
          {mutation.isError && (
            <p role="alert" className="w-full rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark">
              {(mutation.error as Error).message}
            </p>
          )}
          <Button variant="ghost" onClick={back}>
            Annuler
          </Button>
          {notStarted && <p className="ml-auto text-sm text-secondary">Le résumé s’enregistre une fois la leçon commencée.</p>}
          <Button
            type="submit"
            form={formId}
            variant="primary"
            className={notStarted ? undefined : 'ml-auto'}
            loading={mutation.isPending}
            disabled={notStarted || !summary.trim() || !nextObjective.trim()}
          >
            {mutation.isPending ? 'Enregistrement…' : 'Enregistrer le résumé'}
          </Button>
        </>
      }
    >
      {student.data && (
        <Card className="space-y-2 bg-surface-indigo p-5 ring-1 ring-primary-100 sm:p-6">
          <p className="flex items-center gap-2 text-eyebrow uppercase text-primary-600">
            <Target size={14} aria-hidden />
            Objectif de ce cours
          </p>
          {previous?.nextObjective ? (
            <>
              <p className="text-lg font-semibold text-heading">{previous.nextObjective}</p>
              <p className="text-[13px] text-secondary">Fixé au résumé du {fmtDate(previous.lessonDate ?? previous.createdAt)}</p>
            </>
          ) : previous ? (
            <p className="text-sm text-secondary">Aucun objectif noté au dernier résumé.</p>
          ) : (
            <p className="text-sm text-secondary">Première séance : pas encore de résumé. Notez vos objectifs dans « Avant la séance ».</p>
          )}
          {/* J35 : le commentaire privé du dernier résumé, avec son objectif. */}
          <PrivateNote text={previous?.privateNote} />
          {student.data.drivingHoursTarget > 0 && (
            <div className="pt-2">
              <ProgressBar
                value={Math.min(100, Math.round((student.data.drivingHoursDone / student.data.drivingHoursTarget) * 100))}
                label="Heures de conduite"
              />
            </div>
          )}
        </Card>
      )}

      <SessionNotesCard
        candidateId={target.candidateId}
        onReuseDuring={(during) => setSummary((current) => (current.trim() ? `${current.trim()}\n${during}` : during))}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="min-w-0 space-y-5">
          <ReferentialBooklet studentId={target.candidateId} />
          <form id={formId} onSubmit={onSubmit} className="space-y-5">
            {!target.lessonId && (
              <Card className="p-5 sm:p-6">
                <SelectField
                  label="Leçon évaluée"
                  required
                  value={chosenLesson ?? ''}
                  onChange={(e) => setChosenLesson(e.target.value)}
                  hint={chosenLesson && chosenLesson === lessonInProgress(lessons.data ?? [])?.id ? 'Leçon en cours, choisie d’office.' : undefined}
                >
                  <option value="">Choisir une leçon</option>
                  {lessons.data?.map((l) => (
                    <option key={l.id} value={l.id}>
                      {new Date(l.startsAt).toLocaleString('fr-FR')} · {l.instructorName}
                    </option>
                  ))}
                </SelectField>
                {lessons.isError && (
                  <Button variant="link" size="sm" onClick={() => void lessons.refetch()} className="mt-2">
                    Réessayer
                  </Button>
                )}
              </Card>
            )}

            <Card className="space-y-5 p-5 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-title-sm text-heading">Objectif travaillé</h2>
                <SeenByStudent />
              </div>
              <TextAreaField
                label="Synthèse de la séance *"
                required
                rows={4}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Points travaillés, progrès, axes d'amélioration…"
                className="text-[15px]"
              />
              {ratingOffered && (
                <fieldset>
                  <legend className="mb-1 flex flex-wrap items-center gap-2 text-[12.5px] font-medium text-secondary">
                    Note de la séance
                    {!ratingShared && (
                      <span className="inline-flex items-center gap-1 rounded-pill bg-surface-2 px-2 py-0.5 text-[11.5px] font-medium text-secondary">
                        <Lock size={12} aria-hidden />
                        Pas montrée à l'élève
                      </span>
                    )}
                  </legend>
                  {/* J36 : ce que la note mesure, pour que moniteurs et élèves la lisent de la même façon. */}
                  <p className="mb-1 text-xs text-muted">{SESSION_RATING_MEANING}</p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n === rating ? 0 : n)}
                        aria-label={`Note ${n}`}
                        aria-pressed={n <= rating}
                        className="grid h-11 w-11 place-items-center rounded-pill hover:bg-surface-light"
                      >
                        <Star size={26} className={n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'} aria-hidden />
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}
            </Card>

            <Card className="space-y-5 p-5 sm:p-6">
              <h2 className="text-title-sm text-heading">Prochain objectif à travailler</h2>
              <TextAreaField
                label="Objectif du prochain cours *"
                required
                maxLength={1000}
                rows={2}
                value={nextObjective}
                onChange={(e) => setNextObjective(e.target.value)}
                className="text-[15px]"
              />
              <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-row border border-line px-4 py-3 text-sm font-medium text-body has-[:checked]:border-success has-[:checked]:bg-success-light">
                <input type="checkbox" checked={readyForExam} onChange={(e) => setReadyForExam(e.target.checked)} className="h-5 w-5 accent-[#10B981]" />
                <span className="flex-1">Cet élève est prêt(e) pour l'examen</span>
                <SeenByStudent />
              </label>
              {readyForExam && (
                <TextAreaField
                  label="Motif de l’avis d’examen (facultatif)"
                  maxLength={1000}
                  rows={2}
                  value={examReason}
                  onChange={(e) => setExamReason(e.target.value)}
                  className="text-[15px]"
                />
              )}
              <TextAreaField
                label="Action à signaler au secrétariat (facultatif)"
                maxLength={1000}
                rows={2}
                value={secretaryAlert}
                onChange={(e) => setSecretaryAlert(e.target.value)}
                placeholder="Uniquement les informations nécessaires au suivi administratif"
                className="text-[15px]"
              />
            </Card>

            <Card className="space-y-4 p-5 ring-1 ring-primary-100 sm:p-6">
              <div className="flex items-start gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
                  <Lock size={16} aria-hidden />
                </span>
                <div>
                  <h2 className="text-title-sm text-heading">Commentaire privé</h2>
                  <p className="mt-0.5 text-sm text-secondary">Visible par les moniteurs de l’élève. L’élève ne le voit pas.</p>
                </div>
              </div>
              <TextAreaField
                label="Commentaire privé (facultatif)"
                hint="Restez factuel."
                maxLength={1000}
                rows={3}
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                className="text-[15px]"
              />
            </Card>
          </form>
        </div>
        {student.data && (
          <Card className="space-y-3 p-5 sm:p-6">
            <h2 className="text-title-sm text-heading">Résumés des séances</h2>
            {bilans.length === 0 ? (
              <p className="rounded-row bg-surface-light px-4 py-6 text-center text-sm text-secondary">Aucun résumé enregistré pour cet élève.</p>
            ) : (
              <ul className="space-y-3">
                {bilans.map((b) => (
                  <BilanItem key={b.id} b={b} />
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={leaving}
        title="Quitter sans enregistrer ?"
        confirmLabel="Quitter"
        tone="danger"
        onConfirm={() => {
          setLeaving(false);
          close();
        }}
        onCancel={() => setLeaving(false)}
      >
        Le résumé en cours de saisie sera perdu.
      </ConfirmDialog>
    </PageShell>
  );
}
